import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Download icon
const DownloadIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

// Check icon
const CheckIcon = ({ className = "w-10 h-10" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// Microphone Icon
const MicIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);

const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const [issue, setIssue] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [purchaseEmail, setPurchaseEmail] = useState("");
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');
  const issueId = searchParams.get('issue_id');
  const emailParam = searchParams.get('email');

  useEffect(() => {
    checkAuth();
    verifyPurchase();
  }, [sessionId, issueId, checkAuth, verifyPurchase]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
      if (!purchaseEmail) {
        setPurchaseEmail(response.data.email);
      }
    } catch (error) {
      setCurrentUser(null);
    }
  }, [purchaseEmail]);

  const verifyPurchase = useCallback(async () => {
    try {
      // If coming from Stripe, check payment status
      if (sessionId) {
        const statusResponse = await api.get(`/magazine/checkout/status/${sessionId}`);
        
        if (statusResponse.data.payment_status === 'paid') {
          // Get issue details from metadata
          const issueIdFromStripe = statusResponse.data.metadata.issue_id;
          const emailFromStripe = statusResponse.data.metadata.user_email;
          
          setPurchaseEmail(emailFromStripe);
          
          // Fetch all issues and find the matching one
          const issuesResponse = await api.get('/magazine/issues');
          const foundIssue = issuesResponse.data.find(i => i.issue_id === issueIdFromStripe);
          
          if (foundIssue) {
            setIssue(foundIssue);
            setHasAccess(true);
          }
        } else {
          toast.error("Payment not completed yet");
          // Poll for a few seconds in case webhook is slow
          setTimeout(verifyPurchase, 2000);
        }
      } else if (issueId && emailParam) {
        // Direct verification for existing purchases
        const verifyResponse = await api.post(
          `/magazine/verify-purchase?issue_id=${issueId}&user_email=${emailParam}`
        );
        
        setPurchaseEmail(emailParam);
        
        if (verifyResponse.data.has_access) {
          const issuesResponse = await api.get('/magazine/issues');
          const foundIssue = issuesResponse.data.find(i => i.issue_id === issueId);
          if (foundIssue) {
            setIssue(foundIssue);
            setHasAccess(true);
          }
        }
      }
    } catch (error) {
      console.error("Failed to verify purchase:", error);
      toast.error("Failed to verify purchase");
    } finally {
      setLoading(false);
    }
  }, [sessionId, issueId, emailParam]);

  const handleDownload = async () => {
    if (!issue || !purchaseEmail) {
      toast.error("Unable to download. Please contact support.");
      return;
    }

    setDownloading(true);
    try {
      const response = await api.get(
        `/magazine/download/${issue.issue_id}`,
        {
          params: { user_email: purchaseEmail },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${issue.slug}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      toast.error("Download failed", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="magazine" />
        <div className="pt-24 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          <p className="mt-4 text-gray-400">Verifying your purchase...</p>
        </div>
      </div>
    );
  }

  if (!hasAccess || !issue) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="magazine" />
        <div className="pt-24 px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/20 mb-4">
              <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Purchase Not Found</h2>
            <p className="text-gray-400 mb-6">
              We couldn't verify your purchase. If you just completed payment, 
              please wait a moment and refresh the page.
            </p>
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={verifyPurchase}
                variant="outline"
                className="border-purple-500/50 text-purple-400"
              >
                Retry Verification
              </Button>
              <Link to="/magazine">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  Back to Magazine
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="purchase-success-page">
      <SharedNavigation currentUser={currentUser} activePage="magazine" />

      <section className="pt-24 px-4 pb-12">
        <div className="max-w-3xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-4">
              <CheckIcon className="text-green-400" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="success-title">
              Purchase Successful!
            </h1>
            <p className="text-gray-300 text-lg">Thank you for your purchase</p>
            {purchaseEmail && (
              <p className="text-gray-500 text-sm mt-2">
                Confirmation sent to: {purchaseEmail}
              </p>
            )}
          </div>

          {/* Issue Details & Download */}
          <Card
            className="p-8 mb-8 bg-gray-900/50 border-purple-900/30"
            data-testid="purchase-details"
          >
            <div className="flex flex-col sm:flex-row gap-6">
              <img
                src={`${API}/magazine/public/cover/${issue.cover_image}`}
                alt={issue.title}
                className="w-40 h-auto rounded-lg self-center sm:self-start"
                data-testid="issue-cover"
              />
              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl font-bold text-white mb-2" data-testid="issue-title">
                  {issue.title}
                </h2>
                <p className="text-gray-400 mb-4">
                  {new Date(issue.issue_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long'
                  })}
                </p>
                <p className="text-gray-300 mb-6 line-clamp-3">{issue.description}</p>
                
                <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                    data-testid="download-pdf-btn"
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" />
                    {downloading ? "Downloading..." : "Download PDF"}
                  </Button>
                  
                  <Link to={`/magazine/issue/${issue.slug}`}>
                    <Button variant="outline" className="border-purple-500/50 text-purple-400">
                      View Issue Page
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </Card>

          {/* What's Included */}
          <Card className="p-6 mb-8 bg-gray-900/30 border-gray-800">
            <h3 className="text-lg font-bold text-white mb-4">What's Included</h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </span>
                Full digital magazine PDF download
              </li>
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </span>
                Unlimited downloads with your purchase email
              </li>
              <li className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                  <span className="text-green-400 text-xs">✓</span>
                </span>
                Access to exclusive online content
              </li>
            </ul>
          </Card>

          {/* CTA Section */}
          <Card className="p-8 text-center bg-purple-900/20 border-purple-500/30">
            <h3 className="text-2xl font-bold text-white mb-4">Want to Be Featured?</h3>
            <p className="text-gray-300 mb-6">
              Submit your music to RGM Studio and get a chance to be featured in our next issue!
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/login">
                <Button 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                  data-testid="submit-music-cta"
                >
                  Submit Your Music
                </Button>
              </Link>
              <Link to="/magazine">
                <Button variant="outline" className="border-purple-500/50 text-purple-400">
                  Browse More Issues
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-purple-900/30 bg-black/80 backdrop-blur-xl py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img src="/rgm-logo.png" alt="RGM Studio" className="h-8 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/magazine" className="hover:text-purple-400 transition-colors">
                Magazine
              </Link>
              <Link to="/explore" className="hover:text-purple-400 transition-colors">
                Explore
              </Link>
            </div>
            <p className="text-gray-500 text-sm">© 2025 RGM Studio™</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PurchaseSuccess;
